import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from './db-storage';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

export type TensionContextType =
  | 'misunderstanding'
  | 'tone_issue'
  | 'emotional_distance'
  | 'argument_starting'
  | 'other';

export type TensionEmotion = 'confused' | 'overwhelmed' | 'hurt' | 'frustrated' | 'anxious';

export type TensionRespondRequest = {
  sessionId: string;
  input: {
    situationText: string;
    contextType: TensionContextType;
    emotion: TensionEmotion;
    relationshipMode?: string | null;
    personalization?: {
      relationshipName?: string | null;
      relationshipType?: string | null;
      rtiScore?: number | null;
      checkInStreak?: number | null;
      checkInsLast7d?: number | null;
      rtiTrend?: 'up' | 'down' | 'stable' | null;
      recentRiskLevel?: 'low' | 'medium' | 'high' | null;
      lastInsightSummary?: string | null;
      frequentPatterns?: Array<string>;
      userVoiceHint?: string | null;
    };
  };
  options?: {
    stream?: boolean;
  };
};

export type TensionRespondPayload = {
  ok: boolean;
  sessionId: string;
  response: {
    insight: string;
    reframe: string | null;
    suggestedMessages: string[];
    groundingLine: string | null;
    dynamicOptions?: Array<{
      id: string;
      label: string;
      description: string;
      starter: string;
    }>;
    sharing: {
      sendThis: string;
      shareMomentQuote: string;
      povCaption: string;
    };
  };
  context: {
    classification: string;
    riskLevel: 'low' | 'medium' | 'high';
  };
  memory: {
    emotionTags: string[];
    patternCounts: Record<string, number>;
    lastInsightSummary: string;
  };
  performance: {
    latencyMs: number;
    targetMs: number;
    withinTarget: boolean;
  };
  storage?: {
    readFrom?: 'db' | 'kv';
    writeTo?: 'db' | 'kv';
  };
  atlas?: {
    enabled?: boolean;
    used?: boolean;
    shadowMode?: boolean;
    contextVersion?: string | null;
    latencyMs?: number | null;
    fallbackReason?: 'none' | 'disabled' | 'rollout' | 'missing_url' | 'timeout' | 'error' | 'schema';
  };
};

export type TensionStreamPhase = 'thinking' | 'generating' | 'responding' | 'complete';

export type TensionStreamStateEvent = {
  phase: TensionStreamPhase;
  label: string;
  classification?: string;
  riskLevel?: 'low' | 'medium' | 'high';
};

export type TensionStreamPartialEvent = {
  insight?: string;
  reframe?: string | null;
  suggestedMessages?: string[];
  groundingLine?: string | null;
};

export type TensionSessionUpdatePayload = {
  ok: boolean;
  sessionId: string;
  live: {
    currentSituation: string;
    emotionalSignal: TensionEmotion;
    classification: string;
    riskLevel: 'low' | 'medium' | 'high';
    updatedAt: string;
  };
};

function buildHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${publicAnonKey}`,
    apikey: publicAnonKey,
    ...getAuthHeadersForProtectedApi(),
  };
}

export async function requestTensionResponse(payload: TensionRespondRequest): Promise<TensionRespondPayload> {
  const res = await fetch(`${API_BASE}/tension/respond`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<TensionRespondPayload> & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || 'Failed to generate TENSION response');
  }
  return data as TensionRespondPayload;
}

export async function updateTensionLiveSession(payload: TensionRespondRequest): Promise<TensionSessionUpdatePayload> {
  const res = await fetch(`${API_BASE}/tension/session/update`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<TensionSessionUpdatePayload> & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || 'Failed to update live session context');
  }
  return data as TensionSessionUpdatePayload;
}

type StreamHandlers = {
  onState?: (event: TensionStreamStateEvent) => void;
  onPartial?: (event: TensionStreamPartialEvent) => void;
  onFinal?: (payload: TensionRespondPayload) => void;
};

export async function requestTensionResponseStream(
  payload: TensionRespondRequest,
  handlers?: StreamHandlers,
): Promise<TensionRespondPayload> {
  const res = await fetch(`${API_BASE}/tension/respond/stream`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({
      ...payload,
      options: { ...(payload.options || {}), stream: true },
    }),
  });
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(text || 'Failed to start TENSION stream');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let lastPayload: TensionRespondPayload | null = null;
  let streamError: string | null = null;

  const processChunk = (chunk: string) => {
    buffer += chunk;
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const lines = part.split('\n');
      let eventName = '';
      let dataRaw = '';
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          const piece = line.slice('data:'.length).trim();
          dataRaw = dataRaw ? `${dataRaw}\n${piece}` : piece;
        }
      }
      if (!eventName || !dataRaw || dataRaw === '[DONE]') continue;
      let parsed: any = null;
      try {
        parsed = JSON.parse(dataRaw);
      } catch {
        parsed = null;
      }
      if (!parsed) continue;
      if (eventName === 'state') {
        handlers?.onState?.(parsed as TensionStreamStateEvent);
      } else if (eventName === 'partial') {
        handlers?.onPartial?.(parsed as TensionStreamPartialEvent);
      } else if (eventName === 'tension') {
        lastPayload = parsed as TensionRespondPayload;
        handlers?.onFinal?.(lastPayload);
      } else if (eventName === 'error') {
        streamError = typeof parsed?.error === 'string' && parsed.error.trim()
          ? parsed.error.trim()
          : 'Unknown stream error';
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    processChunk(decoder.decode(value, { stream: true }));
  }
  processChunk(decoder.decode());

  if (!lastPayload) {
    throw new Error(streamError || 'TENSION stream ended without final payload');
  }
  return lastPayload;
}
